import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Heart, Star, Check, Eye, Share2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const ProductCard = ({ product, onLoginRequired, onClick }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      onLoginRequired();
      return;
    }

    setIsAdding(true);
    try {
      const result = await addToCart(product.id, 1);
      setIsAdded(true);
      showToast(result.message, 'success');
      
      setTimeout(() => {
        setIsAdded(false);
      }, 2000);
    } catch (error) {
      if (error.message === 'Please login to add items to cart') {
        onLoginRequired();
      } else {
        showToast(error.message || 'Failed to add item to cart', 'error');
      }
    } finally {
      setIsAdding(false);
    }
  };

  const getCategoryName = () => {
    if (!product.category) return '';
    if (typeof product.category === 'string') return product.category;
    if (typeof product.category === 'object' && product.category.name) return product.category.name;
    return '';
  };

  const getProductImage = () => {
    if (product.image_url) return product.image_url;
    if (product.image) return product.image;

    // صور مخصصة للمنتجات
    const productImages = {
      'Wireless Headphones': 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop&q=80',
      'Smart Watch': 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop&q=80',
      'Laptop': 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=300&fit=crop&q=80',
      'Smartphone': 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&h=300&fit=crop&q=80',
      'AirPods Pro': 'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400&h=300&fit=crop&q=80',
      'Apple Watch Series 9': 'https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?w=400&h=300&fit=crop&q=80',
      '4K Monitor': 'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400&h=300&fit=crop&q=80',
      'Data Science Handbook': 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=300&fit=crop&q=80',
      'JavaScript Programming Book': 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=400&h=300&fit=crop&q=80',
      'Premium Cotton T-shirt': 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=300&fit=crop&q=80',
      'Designer Backpack': 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=300&fit=crop&q=80',
      'Modern Table Lamp': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&q=80',
      'Yoga Mat Premium': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop&q=80',
      'Wireless Gaming Mouse': 'https://images.unsplash.com/photo-1527814050087-3793815479db?w=400&h=300&fit=crop&q=80',
      'Smart Fitness Watch': 'https://images.unsplash.com/photo-1575311373937-040b8e1fd5b6?w=400&h=300&fit=crop&q=80'
    };

    if (productImages[product.name]) {
      return productImages[product.name];
    }

    // البحث بالكلمات المفتاحية
    const name = product.name.toLowerCase();
    
    if (name.includes('headphone') || name.includes('airpods')) {
      return 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('watch')) {
      return 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('laptop')) {
      return 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('phone') || name.includes('smartphone')) {
      return 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('monitor')) {
      return 'https://images.unsplash.com/photo-1527443224154-c4a3942d3acf?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('mouse')) {
      return 'https://images.unsplash.com/photo-1527814050087-3793815479db?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('book')) {
      return 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('shirt') || name.includes('t-shirt')) {
      return 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('backpack') || name.includes('bag')) {
      return 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('lamp')) {
      return 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('yoga') || name.includes('mat')) {
      return 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop&q=80';
    }
    if (name.includes('fitness')) {
      return 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&q=80';
    }

    return 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop&q=80';
  };

  return (
    <motion.div
      className="group relative bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl cursor-pointer"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* صورة المنتج */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        <motion.img
          src={getProductImage()}
          alt={product.name}
          className="w-full h-72 object-cover"
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.08 }}
          transition={{ duration: 0.4 }}
        />
        
        {/* طبقة تدرج */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />

        {/* أزرار التحكم */}
        <motion.div
          className="absolute top-4 right-4 flex flex-col space-y-2"
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setIsLiked(!isLiked);
            }}
            className={`p-3 rounded-full backdrop-blur-md transition-all duration-300 ${
              isLiked 
                ? 'bg-red-500 text-white shadow-lg scale-110' 
                : 'bg-white/90 text-gray-700 hover:bg-white hover:scale-110'
            }`}
            whileHover={{ scale: 1.15, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
          </motion.button>
          
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="p-3 rounded-full bg-white/90 text-gray-700 hover:bg-white hover:scale-110 backdrop-blur-md transition-all duration-300"
            whileHover={{ scale: 1.15, rotate: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Share2 size={18} />
          </motion.button>
        </motion.div>

        {/* زر Quick View */}
        <motion.div
          className="absolute bottom-4 left-4 right-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: isHovered ? 0 : 20, opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="w-full bg-white/95 backdrop-blur-sm text-gray-900 py-2 rounded-full font-medium hover:bg-white transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <Eye size={16} />
            <span>Quick View</span>
          </button>
        </motion.div>
      </div>

      {/* معلومات المنتج */}
      <div className="p-6">
        {/* الفئة */}
        {getCategoryName() && (
          <motion.span
            className="inline-block px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 text-xs font-bold uppercase tracking-wider rounded-full mb-3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
          >
            {getCategoryName()}
          </motion.span>
        )}

        {/* اسم المنتج */}
        <motion.h3
          className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          {product.name}
        </motion.h3>

        {/* وصف المنتج */}
        <motion.p
          className="text-gray-600 text-sm mb-4 line-clamp-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {product.description}
        </motion.p>

        {/* التقييم */}
        <motion.div
          className="flex items-center justify-between mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.8 + (i * 0.1), type: "spring", stiffness: 200 }}
              >
                <Star
                  size={16}
                  className={`${
                    i < (product.rating || 4) 
                      ? 'text-yellow-400 fill-current' 
                      : 'text-gray-300'
                  }`}
                />
              </motion.div>
            ))}
          </div>
          <span className="text-sm text-gray-500 font-medium">
            ({product.reviews_count || Math.floor(Math.random() * 100)} reviews)
          </span>
        </motion.div>

        {/* السعر */}
        <motion.div
          className="flex items-center justify-between mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="flex items-center space-x-2">
            <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              ${Number(product.price).toFixed(2)}
            </span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-sm text-gray-500 line-through">
                ${Number(product.original_price).toFixed(2)}
              </span>
            )}
          </div>
        </motion.div>

        {/* زر إضافة للسلة */}
        <motion.button
          onClick={handleAddToCart}
          disabled={isAdding}
          className={`w-full py-3 px-6 rounded-2xl font-bold text-lg transition-all duration-300 ${
            isAdded
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white scale-105'
              : 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 hover:scale-105 hover:shadow-xl'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          {isAdding ? (
            <motion.div
              className="flex items-center justify-center space-x-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <span>Adding...</span>
            </motion.div>
          ) : isAdded ? (
            <motion.div
              className="flex items-center justify-center space-x-2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <Check size={20} />
              <span>Added!</span>
            </motion.div>
          ) : (
            <motion.div className="flex items-center justify-center space-x-2">
              <ShoppingCart size={20} />
              <span>Add to Cart</span>
            </motion.div>
          )}
        </motion.button>

        {/* حالة المخزون */}
        {product.stock !== undefined && (
          <motion.div
            className="mt-3 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {product.stock > 0 ? (
              <span className="text-sm text-green-600 font-semibold bg-green-50 px-3 py-1 rounded-full">
                ✅ In Stock ({product.stock} available)
              </span>
            ) : (
              <span className="text-sm text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full">
                ❌ Out of Stock
              </span>
            )}
          </motion.div>
        )}
      </div>

      {/* تأثير الوهج */}
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)',
          pointerEvents: 'none'
        }}
      />
    </motion.div>
  );
};

export default ProductCard;
